import PropTypes from 'prop-types';

/**
 * ProgressBar component using Tailwind styling
 *
 * A progress bar component for showing upload progress or loading states.
 * Supports different sizes and colors.
 *
 * @param {Object} props
 * @param {number} props.value - Progress value (0-100)
 * @param {string} [props.className] - Additional CSS classes for container
 * @param {string} [props.label] - Optional label to display above progress bar
 * @param {string} [props.size='md'] - Progress bar height ('sm', 'md', 'lg')
 * @param {string} [props.color='blue'] - Progress bar color ('blue', 'green', 'red', 'gray')
 * @param {boolean} [props.showPercentage=false] - Show percentage text
 *
 * @example
 * <ProgressBar value={75} label="Uploading file..." showPercentage />
 *
 * @example
 * <ProgressBar value={50} size="sm" color="green" />
 */
export function ProgressBar({
  value,
  className = '',
  label,
  size = 'md',
  color = 'blue',
  showPercentage = false,
}) {
  // Clamp value between 0 and 100
  const normalizedValue = Math.min(Math.max(value, 0), 100);

  // Size classes for bar height
  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2.5',
    lg: 'h-4',
  };

  // Color classes
  const colorClasses = {
    blue: 'bg-primary',
    green: 'bg-green-600',
    red: 'bg-red-600',
    gray: 'bg-neutral',
  };

  return (
    <div className={className}>
      {/* Label and percentage */}
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-2">
          {label && (
            <span className="text-sm text-muted">{label}</span>
          )}
          {showPercentage && (
            <span className="text-sm text-subtle font-medium">
              {normalizedValue}%
            </span>
          )}
        </div>
      )}

      {/* Progress bar container */}
      <div className={`w-full bg-border-subtle rounded-full overflow-hidden ${sizeClasses[size]}`}>
        {/* Progress bar fill */}
        <div
          className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full transition-all duration-300 ease-out`}
          style={{ width: `${normalizedValue}%` }}
          role="progressbar"
          aria-valuenow={normalizedValue}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
    </div>
  );
}

ProgressBar.propTypes = {
  value: PropTypes.number.isRequired,
  className: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  color: PropTypes.oneOf(['blue', 'green', 'red', 'gray']),
  showPercentage: PropTypes.bool,
};
